import React, { useEffect, useState } from 'react';
import ConfirmDialog from './ConfirmDialog';
import RequiredReasonDialog from './RequiredReasonDialog';
import {
  EDIT_REASON_MAX,
  postApprovalDialogCopy,
} from '../constants/postApprovalEdit';

/**
 * THE post-approval pause — ONE dialog for every edit that lands on an order
 * already approved, ordered, delivered or cancelled.
 *
 * Owner, 2026-09-13: *"We need the pause and check … it stops edits after the
 * fact as a supervisor may change the quantity after the buyer has placed the
 * order and not realise it's been placed."* — and *"seems like we should be
 * using common popup style for editing orders etc?"*. The rulings and every
 * string are in `constants/postApprovalEdit.js`; this file only renders them.
 *
 * Used by: `OrderInlineEditor` (the Save choke point), `OrderForm` (removing a
 * saved line), `pages/OrderPage.js` (its Save — replaced a hand-rolled modal).
 *
 * 🔑 **BUILT ON `ConfirmDialog`, NOT BESIDE IT.** Its `promptLabel` textarea is
 * already optional — nothing there gates Confirm on the text — so the
 * always-visible, never-required reason box is `promptLabel` with no
 * `confirmDisabled`, and the focus trap, Escape, overlay close and focus return
 * come with it.
 *
 * 🔑 **THE REASON IS OWNED HERE AND RESET ON EVERY OPEN.** A reason typed for
 * one pause must never be carried into the next, which is exactly what a
 * parent-held string would do when the same dialog opens twice in a session.
 * `onConfirm(reason)` receives the text as typed; trimming and "blank means
 * none" belong to the caller that sends it (and the server trims again).
 *
 * 🔀 **A REMOVAL DEMANDS THE REASON (owner, 2026-09-28: *"requires a note"*).**
 * When the copy says `reasonRequired`, the SAME state renders through
 * `RequiredReasonDialog` — the house definition of "required" (Confirm disabled
 * until the TRIMMED text is non-empty, `onConfirm(trimmed)`) — rather than a
 * second copy of that gate here. The Save pause keeps the optional box. Chosen
 * over calling `RequiredReasonDialog` from `OrderForm` directly because this
 * component already owns the stage sentence and the reset-on-open reason state;
 * a second call site would fork both.
 *
 * Props: `open`, `status`, `action` ('save' | 'remove-item'), optional
 * `itemLabel`, `amountText` (formatted money), `adjustmentsText` (the part of
 * it that is cascaded price adjustments) and `countsInBudget` (removal only), `onConfirm(reason)`, `onCancel`, `busy`.
 */
export default function PostApprovalEditDialog({
  open,
  status,
  action = 'save',
  itemLabel,
  amountText,
  adjustmentsText,
  countsInBudget = false,
  onConfirm,
  onCancel,
  busy = false,
}) {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const copy = postApprovalDialogCopy({ status, action, itemLabel, amountText, adjustmentsText, countsInBudget });

  const message = (
    <span data-testid="post-approval-dialog-message">
      <strong className="block text-gray-800 dark:text-gray-100">{copy.stageSentence}</strong>
      <span className="block mt-1">{copy.actionSentence}</span>
    </span>
  );
  const shared = {
    open,
    title: copy.title,
    variant: copy.variant,
    message,
    confirmLabel: copy.confirmLabel,
    cancelLabel: copy.cancelLabel,
    busy,
    onCancel: () => onCancel?.(),
  };

  if (copy.reasonRequired) {
    return (
      <RequiredReasonDialog
        {...shared}
        reason={reason}
        onReasonChange={setReason}
        reasonLabel={copy.reasonLabel}
        reasonMaxLength={EDIT_REASON_MAX}
        onConfirm={(trimmed) => onConfirm?.(trimmed)}
      />
    );
  }

  return (
    <ConfirmDialog
      {...shared}
      promptLabel={copy.reasonLabel}
      promptValue={reason}
      onPromptChange={setReason}
      promptMaxLength={EDIT_REASON_MAX}
      onConfirm={() => onConfirm?.(reason)}
    />
  );
}
